import React, { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";

/**
 * Email chips input with autocomplete from a directory.
 *
 * Props:
 *   value: string[]                            - current list of emails
 *   onChange: (string[]) => void
 *   suggestions: [{ email, name?, role? }]    - directory entries
 *   placeholder?: string
 *   testid?: string
 *   compact?: boolean                          - smaller padding
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CcInput({
    value = [],
    onChange,
    suggestions = [],
    placeholder = "correo@ejemplo.com",
    testid,
    compact = false,
}) {
    const [text, setText] = useState("");
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const wrapRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const selectedSet = useMemo(() => new Set(value || []), [value]);

    const filtered = useMemo(() => {
        const q = text.trim().toLowerCase();
        if (!q) return [];
        const out = [];
        for (const s of suggestions || []) {
            if (!s.email || selectedSet.has(s.email)) continue;
            const hay = `${s.name || ""} ${s.email}`.toLowerCase();
            if (hay.includes(q)) out.push(s);
            if (out.length >= 8) break;
        }
        return out;
    }, [text, suggestions, selectedSet]);

    const addEmail = (raw) => {
        const e = (raw || "").trim().replace(/[,;]+$/, "");
        if (!e) return;
        if (!EMAIL_RE.test(e)) {
            // ignore invalid silently — user keeps typing
            return;
        }
        if (selectedSet.has(e)) {
            setText("");
            return;
        }
        onChange?.([...(value || []), e]);
        setText("");
        setActive(0);
    };

    const removeEmail = (e) => {
        onChange?.((value || []).filter((x) => x !== e));
    };

    const onKeyDown = (e) => {
        if (e.key === "Backspace" && !text && (value || []).length > 0) {
            // remove last chip
            const arr = [...(value || [])];
            arr.pop();
            onChange?.(arr);
            return;
        }
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            if (open && filtered[active]) {
                addEmail(filtered[active].email);
            } else if (text.trim()) {
                addEmail(text);
            }
            return;
        }
        if (e.key === "ArrowDown" && filtered.length > 0) {
            e.preventDefault();
            setActive((a) => Math.min(filtered.length - 1, a + 1));
            return;
        }
        if (e.key === "ArrowUp" && filtered.length > 0) {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
            return;
        }
        if (e.key === "Escape") {
            setOpen(false);
        }
    };

    const onChangeText = (v) => {
        setText(v);
        setOpen(!!v.trim());
        setActive(0);
        // auto-add if user typed comma at end
        if (v.endsWith(",") || v.endsWith(";")) {
            const candidate = v.slice(0, -1).trim();
            if (EMAIL_RE.test(candidate)) addEmail(candidate);
        }
    };

    const onBlur = () => {
        // small delay so option click can be processed
        setTimeout(() => {
            if (text.trim() && EMAIL_RE.test(text.trim())) addEmail(text);
        }, 120);
    };

    const padding = compact ? "4px 6px" : "6px 8px";

    return (
        <div ref={wrapRef} className="cc-input" data-testid={testid} style={{ position: "relative" }}>
            <div
                className="input"
                style={{
                    width: "100%", minHeight: compact ? 32 : 40, padding,
                    display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center",
                    cursor: "text",
                }}
                onClick={() => inputRef.current?.focus()}
            >
                {(value || []).map((e) => (
                    <span
                        key={e}
                        style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            background: "var(--bg-2)", border: "1px solid var(--border-strong)",
                            borderRadius: 999, padding: "2px 6px 2px 8px", fontSize: 11.5,
                            fontFamily: "monospace, JetBrains Mono",
                        }}
                        data-testid={testid ? `${testid}-chip-${e}` : undefined}
                    >
                        {e}
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={(ev) => { ev.stopPropagation(); removeEmail(e); }}
                            onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.stopPropagation(); removeEmail(e); } }}
                            title="Quitar"
                            style={{ cursor: "pointer", color: "var(--text-muted)", display: "inline-flex", alignItems: "center" }}
                            data-testid={testid ? `${testid}-remove-${e}` : undefined}
                        >
                            <X size={11} />
                        </span>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={text}
                    onChange={(e) => onChangeText(e.target.value)}
                    onKeyDown={onKeyDown}
                    onFocus={() => text.trim() && setOpen(true)}
                    onBlur={onBlur}
                    placeholder={(value || []).length === 0 ? placeholder : ""}
                    style={{
                        flex: 1, minWidth: 140, border: 0, outline: 0, background: "transparent",
                        color: "var(--text)", fontSize: compact ? 11.5 : 12, padding: "2px 0",
                    }}
                    data-testid={testid ? `${testid}-input` : undefined}
                />
            </div>

            {open && filtered.length > 0 && (
                <div
                    style={{
                        position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 60,
                        background: "var(--bg-2)", border: "1px solid var(--border-strong)", borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.4)", padding: 4, maxHeight: 240, overflow: "auto",
                    }}
                    data-testid={testid ? `${testid}-popup` : undefined}
                >
                    {filtered.map((s, idx) => (
                        <div
                            key={s.email}
                            onMouseDown={(e) => { e.preventDefault(); addEmail(s.email); }}
                            onMouseEnter={() => setActive(idx)}
                            style={{
                                display: "flex", alignItems: "center", gap: 8,
                                padding: "6px 8px", cursor: "pointer", borderRadius: 4,
                                background: idx === active ? "rgba(220,38,38,0.08)" : "transparent",
                            }}
                            data-testid={testid ? `${testid}-option-${s.email}` : undefined}
                        >
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13 }}>{s.name || s.email}</div>
                                <div className="text-muted mono text-xs">{s.email}{s.role ? ` · ${s.role}` : ""}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
